import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Pill, SectionHead } from "../../shared/ui";
import { motionOn, stagger, usePlay } from "../../shared/motion";
import "./bento.css";

// Mock customers (constants — not translated). `a/b` feed hero.mock.months ("N/M oy").
const CUSTOMERS = [
  { av: "av-amber", ini: "MR", name: "Malika Rahimova", ds: "iPhone 15 Pro", months: [3, 9], amt: "− $450", neg: true },
  { av: "av-green", ini: "SV", name: "Sardor Valiyev", ds: "Chevrolet Cobalt", months: [7, 16], amt: "+ $600", neg: false },
  { av: "av-blue", ini: "DK", name: "Dilnoza Karimova", ds: null, months: null, amt: "− $250", neg: true },
] as const;

const PAID = "$150";

type PayState = { seg: 0 | 1; digits: string; rest: string; upd: number; typing: boolean };

// SSG / reduced-motion / no-JS state = the final frame (matches the prototype markup).
const FINAL: PayState = { seg: 1, digits: PAID, rest: "$450", upd: 0, typing: false };

// Flexible-payment demo: segment thumb slides to "Partial", "$150" is typed per char, then the
// remaining debt updates. Starts from the final state; only resets in an effect when motion is on.
function PayDemo({ card }: { card: React.RefObject<HTMLElement | null> }) {
  const { t } = useTranslation();
  const [s, setS] = useState<PayState>(FINAL);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    // Reset only after hydration (SSG markup stays the final frame) — a deliberate effect-time setState.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (motionOn()) setS({ seg: 0, digits: "", rest: "$600", upd: 0, typing: true });
    const list = timers.current;
    return () => list.forEach((id) => window.clearTimeout(id));
  }, []);

  usePlay(card, () => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
    const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms));
    setS({ seg: 0, digits: "", rest: "$600", upd: 0, typing: true });
    at(700, () => setS((p) => ({ ...p, seg: 1 })));
    [...PAID].forEach((_, i) =>
      at(1050 + i * 240, () => setS((p) => ({ ...p, digits: PAID.slice(0, i + 1) }))),
    );
    at(2100, () => setS((p) => ({ ...p, rest: "$450", upd: p.upd + 1 })));
  });

  const segs = [t("bento.seg_full"), t("bento.seg_partial"), t("bento.seg_defer")];
  return (
    <>
      <div className={`dseg${s.seg === 0 ? " at0" : ""}`}>
        <i className="th" />
        {segs.map((label, i) => (
          <span key={i} className={i === s.seg ? "on" : undefined}>
            {label}
          </span>
        ))}
      </div>
      <div className="fld">
        <label>{t("bento.pay_amount")}</label>
        <div className="box">
          {s.typing
            ? s.digits.split("").map((ch, i) => (
                <span key={i} className="dg">
                  {ch}
                </span>
              ))
            : s.digits}
        </div>
        <small key={s.upd} className={s.upd ? "upd" : undefined}>
          {t("bento.rest", { v: s.rest })}
        </small>
      </div>
    </>
  );
}

const BentoFeatures = () => {
  const { t } = useTranslation();
  const payCard = useRef<HTMLElement>(null);

  const text = (key: string) => (
    <div>
      <h3>{t(`featuresCards.${key}.title`)}</h3>
      <p>{t(`featuresCards.${key}.subtitle`)}</p>
    </div>
  );

  return (
    <section className="sec" id="features">
      <div className="wrap">
        <SectionHead
          eyebrow={t("main.features")}
          title={t("main.features_title")}
          sub={t("main.features_subtitle")}
        />
        <div className="bento">
          <article className="bc s4" data-r>
            <div className="well" aria-hidden="true">
              <div className="chips">
                <span className="chip on">
                  {t("bento.chip_all")}
                  <span className="c">24</span>
                </span>
                <span className="chip">
                  {t("bento.chip_debtors")}
                  <span className="c">9</span>
                </span>
                <span className="chip">
                  {t("bento.chip_overdue")}
                  <span className="c red">3</span>
                </span>
              </div>
              {CUSTOMERS.map((c, i) => (
                <div className="prow" style={stagger(i)} key={c.ini}>
                  <span className={`av ${c.av}`}>{c.ini}</span>
                  <div className="grow">
                    <div className="nm">{c.name}</div>
                    <div className="ds">
                      {c.months
                        ? `${c.ds} · ${t("hero.mock.months", { a: c.months[0], b: c.months[1] })}`
                        : t("bento.cash_loan")}
                    </div>
                  </div>
                  <div className="sg">
                    <b className={c.neg ? "neg" : "pos"}>{c.amt}</b>
                    <small>{c.neg ? t("bento.owes") : t("bento.paid")}</small>
                  </div>
                </div>
              ))}
            </div>
            {text("card3")}
          </article>

          <article className="bc s2" data-r style={stagger(1)}>
            <div className="well" aria-hidden="true">
              <div className="rem">
                <span className="tile amber">
                  <Icon name="i-bell" />
                </span>
                <div className="grow">
                  <div className="ds">{t("bento.toast_label")}</div>
                  <div className="nm">{t("bento.toast_text")}</div>
                </div>
              </div>
              <div className="toast">
                <span className="disc">
                  <Icon name="i-check" />
                </span>
                {t("bento.toast_title")}
              </div>
            </div>
            {text("card4")}
          </article>

          <article className="bc s3" data-r>
            <div className="well" aria-hidden="true">
              <div className="stp">
                <div>
                  <i>
                    <b />
                  </i>
                  {t("bento.step_client")}
                </div>
                <div>
                  <i>
                    <b />
                  </i>
                  {t("bento.step_type")}
                </div>
                <div>
                  <i>
                    <b />
                  </i>
                  {t("bento.step_finance")}
                </div>
                <div className="cur">
                  <i>
                    <b />
                  </i>
                  {t("bento.step_docs")}
                </div>
              </div>
              <div className="mini">
                <span className="tile">
                  <Icon name="i-car" />
                </span>
                <div className="grow">
                  <div className="nm">Chevrolet Cobalt</div>
                  <div className="ds">{t("bento.monthly", { p: "$600", n: 16 })}</div>
                </div>
                <Pill tone="active">{t("bento.sign_status")}</Pill>
              </div>
            </div>
            {text("card1")}
          </article>

          <article className="bc s3" data-r data-play="pay" style={stagger(1)} ref={payCard}>
            <div className="well" aria-hidden="true">
              <PayDemo card={payCard} />
            </div>
            {text("card2")}
          </article>

          <article className="bc s3 soon" data-r>
            <span className="qt">
              <Icon name="i-lock" />
            </span>
            <div>
              <Pill tone="closed">{t("featuresCards.card5.soon")}</Pill>
              <h3>{t("featuresCards.card5.title")}</h3>
              <p>{t("featuresCards.card5.subtitle")}</p>
            </div>
          </article>

          <article className="bc s3 soon" data-r style={stagger(1)}>
            <span className="qt">
              <Icon name="i-clock" />
            </span>
            <div>
              <Pill tone="closed">{t("featuresCards.card6.soon")}</Pill>
              <h3>{t("featuresCards.card6.title")}</h3>
              <p>{t("featuresCards.card6.subtitle")}</p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default BentoFeatures;
